import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createOrUpdatePricingCalculator,
  getCurrentPricingCalculator,
  getAllPricingCalculators,
} from './api';
import { PricingCalculator } from './components/MonthlyTrendChart';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { CreatePricingCalculatorInput } from '@pages/price-calculator';
import { queryClient } from '@shared/queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { getCustomerRetentionByMonth } from '@features/visits/api';
import { CustomerRetentionMetrics } from './entity';
import { Dayjs } from 'dayjs';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

const QUERY_KEYS = {
  all: ['pricing-calculator'] as const,
  current: (month?: string) => ['pricing-calculator', 'current', month] as const,
  byId: (id: string) => ['pricing-calculator', id] as const,
};

export const useCurrentPricingCalculatorQuery = (month?: Date) => {
  const monthKey = month ? month.toISOString().slice(0, 7) : undefined;

  return useQuery<PricingCalculator>({
    queryKey: QUERY_KEYS.current(monthKey),
    queryFn: () => getCurrentPricingCalculator(month),
    retry: false,
  });
};

export const useAllPricingCalculatorsQuery = () => {
  return useQuery<PricingCalculator[]>({
    queryKey: QUERY_KEYS.all,
    queryFn: () => getAllPricingCalculators(),
  });
};

export const useCustomerRetentionByMonthQuery = (month: Dayjs) => {
  return useQuery<CustomerRetentionMetrics>({
    queryKey: ['customer-retention', month.format('YYYY-MM')],
    queryFn: () => getCustomerRetentionByMonth(month),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateOrUpdatePricingCalculatorMutation = () => {
  const addSnackbarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('pricing.mutation');
  const { trackEvent } = useAnalytics();

  return useMutation<PricingCalculator, Error, CreatePricingCalculatorInput>({
    mutationFn: (data: CreatePricingCalculatorInput) => createOrUpdatePricingCalculator(data),
    onSuccess: (_, variables) => {
      const costs = variables.overheadCosts.filter(cost => cost.name.trim() || cost.amount > 0);
      trackEvent('pricing_calculator_used', {
        costs_count: costs.length,
        has_costs: costs.length > 0,
        costs_total: costs.reduce((sum, cost) => sum + cost.amount, 0),
        calculation_month: variables.calculationMonth?.toISOString().slice(0, 7),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
      // Invalidate all current month queries
      queryClient.invalidateQueries({ queryKey: ['pricing-calculator', 'current'] });
      addSnackbarMessage({
        text: t('saveSuccess'),
        type: 'success',
      });
    },
    onError: () => {
      addSnackbarMessage({
        text: t('saveError'),
        type: 'error',
      });
    },
  });
};
